<script setup lang="ts">
// McpPlanChip (ORP-21466) — the plan + limits card that closes the MCP rail.
//
// It reads ONE source: `useMcpPlan()`, whose numbers all come from `credits/service/mcpPlans.ts`.
// Nothing about a plan is authored here, because the seat figures are still a draft and a second
// copy of them would be the thing that goes stale.
//
// ⚠ The daily tool-call quota is deliberately absent (ratified 2026-08-23): no MCP screen shows a
// call counter.
import { computed } from 'vue'
import { RouterLink } from 'vue-router'

import { useNavLabels } from '@/app/navigation/useNavLabels'
import { MCP_PLAN_LABEL_KEYS, useMcpPlan } from '@/features/credits'
import { useI18n } from '@/shared/i18n'

const { t } = useI18n()
// The tier key is data from the plan config, not template-authored copy — same seam the nav tree
// uses for its own dynamic label keys.
const { labelOf } = useNavLabels()
const { summary } = useMcpPlan()

const planLabel = computed(() =>
  summary.value.tier === null
    ? t('shell.mcp.plan.unknown')
    : labelOf(MCP_PLAN_LABEL_KEYS[summary.value.tier]),
)

/**
 * "5 connections, 3 users" — the two LIMITS, per the design and AC 2.
 *
 * A limit that has not resolved is omitted rather than rendered as a zero or an em dash: on the
 * Read plan the connection ceiling is the price meter, and showing a wrong one would misprice the
 * account in the user's head. With neither known the whole line disappears and the chip is still
 * a truthful "you are on the Read plan, here is Upgrade".
 */
const limits = computed(() => {
  const parts: string[] = []
  const connections = summary.value.connections?.limit
  const users = summary.value.users?.limit
  if (connections !== null && connections !== undefined) {
    parts.push(t('shell.mcp.plan.connections', { count: connections }, connections))
  }
  if (users !== null && users !== undefined) {
    parts.push(t('shell.mcp.plan.users', { count: users }, users))
  }
  return parts
})

const limitsLine = computed(() =>
  limits.value.length > 0 ? t('shell.mcp.plan.limits', { limits: limits.value.join(', ') }) : '',
)
</script>

<template>
  <section
    class="mcp-plan-chip"
    data-testid="mcp-plan-chip"
    :aria-label="t('shell.mcp.plan.label')"
  >
    <p class="mcp-plan-chip__plan" data-testid="mcp-plan-chip-plan">{{ planLabel }}</p>
    <p v-if="limitsLine" class="mcp-plan-chip__limits" data-testid="mcp-plan-chip-limits">
      {{ limitsLine }}
    </p>
    <RouterLink
      class="mcp-plan-chip__upgrade"
      data-testid="mcp-plan-chip-upgrade"
      :to="{ name: 'McpPlanBilling' }"
    >
      {{ t('shell.mcp.plan.upgrade') }}
    </RouterLink>
  </section>
</template>

<style scoped>
.mcp-plan-chip {
  margin-block-start: auto;
  padding: var(--ui-space-sm);
  border: 1px solid var(--ui-color-border);
  border-radius: var(--ui-radius-md);
}
.mcp-plan-chip__plan {
  margin: 0;
  color: var(--ui-color-fg);
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.25;
}
.mcp-plan-chip__limits {
  /* The design's #6B6980 body grey maps to the semantic muted token: raw hex outside
     `shared/ui/tokens/` fails `check:design-token-ownership`. */
  margin: 0.125rem 0 var(--ui-space-xs);
  color: var(--ui-color-muted);
  font-size: 0.6875rem;
  line-height: 1.4;
}
.mcp-plan-chip__upgrade {
  display: block;
  padding: var(--ui-space-2xs) 0;
  border-radius: var(--ui-radius-full);
  background: var(--ui-color-primary);
  color: var(--ui-color-primary-fg);
  font-size: 0.6875rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
}
.mcp-plan-chip__upgrade:focus-visible {
  outline: 2px solid var(--ui-color-info);
  outline-offset: 2px;
}
</style>
